import { YearInReviewStatsQuery } from "@/__generated__/graphql";
import { UserProfile } from "@/__generated__/graphql";
import { Alert, Box, Card, CircularProgress, Stack, Typography } from "@mui/joy";
import { FormattedMessage, useIntl } from "react-intl";
import ShareImageGenerator from "../components/ShareImageGenerator";
import "swiper/css";
import "swiper/css/pagination";
import { Swiper, SwiperSlide, SwiperRef } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { useRef, useState, useEffect, useMemo } from "react";

// Функция для правильного склонения русских слов
const pluralize = (count: number, one: string, few: string, many: string): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod100 >= 11 && mod100 <= 19) {
    return many;
  }
  if (mod10 === 1) {
    return one;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }
  return many;
};

// Функция для определения количества слов в строке
const getWordCount = (text: string | number): number => {
  if (typeof text !== "string") return 0;
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
};

// Компонент для автоматического масштабирования текста через SVG
function AutoScaleText({
  text,
  baseFontSize = 64,
  color = "white",
}: {
  text: string | number;
  baseFontSize?: number;
  color?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scale, setScale] = useState(1);

  const wordCount = getWordCount(text);
  const shouldBreakLines = wordCount === 2;
  const textString = typeof text === "string" ? text : String(text);
  const words = useMemo(() => (typeof text === "string" ? text.trim().split(/\s+/) : [String(text)]), [text]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    // Используем ResizeObserver для отслеживания изменений размера
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current) return;

      // Создаем временный элемент для измерения естественной ширины текста
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.visibility = "hidden";
      tempDiv.style.whiteSpace = "nowrap";
      tempDiv.style.fontSize = `${baseFontSize}px`;
      tempDiv.style.fontWeight = "bold";
      tempDiv.style.fontFamily = "inherit";

      if (shouldBreakLines) {
        // Находим самое длинное слово для определения масштаба
        const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b));
        tempDiv.textContent = longestWord;
      } else {
        tempDiv.textContent = textString;
      }

      document.body.appendChild(tempDiv);
      const naturalWidth = tempDiv.offsetWidth;
      document.body.removeChild(tempDiv);

      const availableWidth = containerWidth - 16;
      if (naturalWidth > 0 && availableWidth > 0) {
        const newScale = Math.min(1, availableWidth / naturalWidth);
        setScale(Math.max(0.4, newScale));
      } else {
        setScale(1);
      }
    };

    if (containerWidth > 0) {
      // Небольшая задержка для корректного измерения
      const timeout = setTimeout(calculateScale, 50);
      return () => clearTimeout(timeout);
    }
  }, [containerWidth, textString, words, shouldBreakLines, baseFontSize]);

  const lineHeight = baseFontSize * 1.2;
  const svgWidth = containerWidth > 0 ? containerWidth - 16 : 100;
  // Высота SVG для правильного центрирования
  const svgHeight = shouldBreakLines ? lineHeight * 2 : lineHeight;

  // Вычисляем вертикальный центр для позиционирования текста
  // С dominantBaseline="middle" координата y - это центр текста по вертикали
  const centerY = svgHeight / 2;
  // Для двух слов: первая строка выше центра на половину межстрочного интервала
  const firstLineY = shouldBreakLines ? centerY - lineHeight / 2 : centerY;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        minHeight: 0,
      }}
    >
      <svg
        width={svgWidth}
        height={svgHeight}
        style={{
          overflow: "visible",
          display: "block",
        }}
      >
        <g transform={`scale(${scale})`}>
          {shouldBreakLines ? (
            words.map((word, index) => (
              <text
                key={index}
                x="0"
                y={firstLineY + lineHeight * index}
                fontSize={baseFontSize}
                fontWeight="bold"
                fill={color}
                dominantBaseline="middle"
              >
                {word}
              </text>
            ))
          ) : (
            <text x="0" y={centerY} fontSize={baseFontSize} fontWeight="bold" fill={color} dominantBaseline="middle">
              {textString}
            </text>
          )}
        </g>
      </svg>
    </Box>
  );
}

type YearInReviewStats = YearInReviewStatsQuery["yearInReviewStats"];

export default function YearInReviewView({
  user,
  data,
  error,
  loading,
}: {
  user: UserProfile;
  data?: YearInReviewStats;
  error?: string;
  loading: boolean;
}) {
  const intl = useIntl();
  const swiperRef = useRef<SwiperRef>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (loading) {
    return (
      <Stack gap={4} p={2} pt={4} pb={12} alignItems="center" justifyContent="center" sx={{ minHeight: "50vh" }}>
        <CircularProgress size="lg" />
        <Typography level="body-lg">
          <FormattedMessage defaultMessage="Загружаем твою статистику..." />
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap={4} p={2} pt={4} pb={12}>
        <Alert color="danger">{error}</Alert>
      </Stack>
    );
  }

  if (!data) {
    return (
      <Stack gap={4} p={2} pt={4} pb={12}>
        <Alert color="warning">
          <FormattedMessage defaultMessage="Не удалось загрузить статистику" />
        </Alert>
      </Stack>
    );
  }

  const totalHoursRounded = Math.round(data.totalHours);
  const hoursWord = pluralize(totalHoursRounded, "час", "часа", "часов");

  // Палитра из 5 гармоничных градиентов с хорошим контрастом
  // Используем цветовую теорию: розовый, фуксия, фиолетовый с разными оттенками
  const colorPalette = [
    "linear-gradient(135deg, #ec4899 0%, #d946ef 100%)", // 1. Яркий розово-фуксиевый
    "linear-gradient(135deg, #a855f7 0%, #c084fc 100%)", // 2. Средний фиолетовый
    "linear-gradient(135deg, #9333ea 0%, #a855f7 100%)", // 3. Темный фиолетовый
    "linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)", // 4. Светлый розовый (для темного текста)
    "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)", // 5. Яркий розовый
  ];

  const statsCards = [
    {
      id: "totalHours",
      title: intl.formatMessage({ defaultMessage: "Всего часов занятий в AMANI за 2025" }),
      titleNode: <FormattedMessage defaultMessage="Всего часов занятий в AMANI за 2025" />,
      value: totalHoursRounded,
      description: intl.formatMessage(
        { defaultMessage: "{hours} {hoursWord} - время для себя и своих целей!" },
        { hours: totalHoursRounded, hoursWord }
      ),
      descriptionNode: (
        <FormattedMessage
          defaultMessage="{hours} {hoursWord} - время для себя и своих целей!"
          values={{ hours: totalHoursRounded, hoursWord }}
        />
      ),
      color: colorPalette[0],
      emoji: "⏰",
    },
    ...(data.favoriteDirection
      ? [
          {
            id: "favoriteDirection",
            title: intl.formatMessage({ defaultMessage: "Любимое направление в AMANI за 2025" }),
            titleNode: <FormattedMessage defaultMessage="Любимое направление в AMANI за 2025" />,
            value: data.favoriteDirection || "",
            description: data.favoriteDirectionCount
              ? (() => {
                  const lessonsWord = pluralize(data.favoriteDirectionCount, "занятие", "занятия", "занятий");
                  return intl.formatMessage(
                    { defaultMessage: "{count} {lessonsWord} по направлению {direction}!" },
                    {
                      count: data.favoriteDirectionCount,
                      lessonsWord,
                      direction: data.favoriteDirection,
                    }
                  );
                })()
              : null,
            descriptionNode: data.favoriteDirectionCount
              ? (() => {
                  const lessonsWord = pluralize(data.favoriteDirectionCount, "занятие", "занятия", "занятий");
                  return (
                    <FormattedMessage
                      defaultMessage="{count} {lessonsWord} по направлению {direction}!"
                      values={{
                        count: data.favoriteDirectionCount,
                        lessonsWord,
                        direction: data.favoriteDirection,
                      }}
                    />
                  );
                })()
              : null,
            color: colorPalette[1],
            emoji: "💃",
          },
        ]
      : []),
    ...(data.busiestMonth
      ? [
          {
            id: "busiestMonth",
            title: intl.formatMessage({ defaultMessage: "Самый насыщенный месяц в AMANI за 2025" }),
            titleNode: <FormattedMessage defaultMessage="Самый насыщенный месяц в AMANI за 2025" />,
            value: data.busiestMonth || "",
            description: data.busiestMonthHours
              ? (() => {
                  const busiestHoursRounded = Math.round(data.busiestMonthHours);
                  const hoursWord = pluralize(busiestHoursRounded, "час", "часа", "часов");
                  return intl.formatMessage(
                    { defaultMessage: "{hours} {hoursWord} занятий за {month}!" },
                    {
                      hours: busiestHoursRounded,
                      hoursWord,
                      month: data.busiestMonth,
                    }
                  );
                })()
              : null,
            descriptionNode: data.busiestMonthHours
              ? (() => {
                  const busiestHoursRounded = Math.round(data.busiestMonthHours);
                  const hoursWord = pluralize(busiestHoursRounded, "час", "часа", "часов");
                  return (
                    <FormattedMessage
                      defaultMessage="{hours} {hoursWord} занятий за {month}!"
                      values={{
                        hours: busiestHoursRounded,
                        hoursWord,
                        month: data.busiestMonth,
                      }}
                    />
                  );
                })()
              : null,
            color: colorPalette[2],
            emoji: "🔥",
          },
        ]
      : []),
    ...(data.favoriteTeacher
      ? [
          {
            id: "favoriteTeacher",
            title: intl.formatMessage({ defaultMessage: "Любимый преподаватель в AMANI за 2025" }),
            titleNode: <FormattedMessage defaultMessage="Любимый преподаватель в AMANI за 2025" />,
            value: data.favoriteTeacher.appName || data.favoriteTeacher.appTitle || "",
            description: data.favoriteTeacherCount
              ? (() => {
                  const lessonsWord = pluralize(data.favoriteTeacherCount, "занятие", "занятия", "занятий");
                  return intl.formatMessage(
                    { defaultMessage: "{count} {lessonsWord} с преподавателем {teacher}!" },
                    {
                      count: data.favoriteTeacherCount,
                      lessonsWord,
                      teacher: data.favoriteTeacher.appName || data.favoriteTeacher.appTitle,
                    }
                  );
                })()
              : null,
            descriptionNode: data.favoriteTeacherCount
              ? (() => {
                  const lessonsWord = pluralize(data.favoriteTeacherCount, "занятие", "занятия", "занятий");
                  return (
                    <FormattedMessage
                      defaultMessage="{count} {lessonsWord} с преподавателем {teacher}!"
                      values={{
                        count: data.favoriteTeacherCount,
                        lessonsWord,
                        teacher: data.favoriteTeacher.appName || data.favoriteTeacher.appTitle,
                      }}
                    />
                  );
                })()
              : null,
            color: colorPalette[3],
            emoji: "👩‍🏫",
          },
        ]
      : []),
    ...(data.favoriteAdministrator
      ? [
          {
            id: "favoriteAdministrator",
            title: intl.formatMessage({ defaultMessage: "Любимый администратор в AMANI за 2025" }),
            titleNode: <FormattedMessage defaultMessage="Любимый администратор в AMANI за 2025" />,
            value: data.favoriteAdministrator.appName || "",
            description: data.favoriteAdministratorCount
              ? intl.formatMessage(
                  { defaultMessage: "{count} столько раз вы виделись в 2025 году!" },
                  { count: data.favoriteAdministratorCount }
                )
              : null,
            descriptionNode: data.favoriteAdministratorCount ? (
              <FormattedMessage
                defaultMessage="{count} столько раз вы виделись в 2025 году!"
                values={{
                  count: data.favoriteAdministratorCount,
                }}
              />
            ) : null,
            color: colorPalette[4],
            emoji: "🤝",
          },
        ]
      : []),
  ];

  return (
    <Stack gap={4} p={2} pt={4} pb={4}>
      <Stack gap={2} alignItems="center">
        <Typography level="h2" textAlign="center">
          <FormattedMessage defaultMessage="Смотри, как прошёл твой год в AMANI! 🎉" />
        </Typography>
      </Stack>

      <Box sx={{ width: "100%", pb: 2, position: "relative" }}>
        {statsCards.length > 1 && (
          <Typography
            level="body-sm"
            sx={{
              textAlign: "center",
              mb: 2,
              color: "neutral.500",
              fontSize: "12px",
            }}
          >
            <FormattedMessage defaultMessage="← Листайте карточки →" />
          </Typography>
        )}
        <Swiper
          ref={swiperRef}
          modules={[Pagination]}
          spaceBetween={16}
          slidesPerView={1}
          pagination={{ clickable: true, dynamicBullets: true }}
          style={{ paddingBottom: "40px" }}
          onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.activeIndex)}
        >
          {statsCards.map((card, index) => (
            <SwiperSlide key={card.id}>
              <Card
                data-card-id={card.id}
                data-card-index={index}
                sx={{
                  p: 4,
                  height: "400px",
                  background: card.color,
                  color: "white",
                  border: "none",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Stack gap={2}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography level="h1" sx={{ fontSize: "48px" }}>
                      {card.emoji}
                    </Typography>
                    <Typography
                      level="title-sm"
                      sx={{
                        color: card.id === "favoriteTeacher" ? "neutral.700" : "rgba(255, 255, 255, 0.9)",
                        fontSize: "14px",
                      }}
                    >
                      {card.titleNode}
                    </Typography>
                  </Stack>
                  {card.id === "favoriteAdministrator" ||
                  card.id === "favoriteTeacher" ||
                  card.id === "favoriteDirection" ||
                  card.id === "busiestMonth" ? (
                    <AutoScaleText
                      text={card.value}
                      color={card.id === "favoriteTeacher" ? "neutral.900" : "white"}
                      baseFontSize={
                        typeof card.value === "string"
                          ? card.value.length > 15
                            ? 48
                            : card.value.length > 10
                            ? 56
                            : 64
                          : 64
                      }
                    />
                  ) : (
                    <Typography
                      level="h1"
                      sx={{
                        color: card.id === "favoriteTeacher" ? "neutral.900" : "white",
                        fontSize: "64px",
                        fontWeight: "bold",
                        lineHeight: 1.1,
                      }}
                    >
                      {card.value}
                    </Typography>
                  )}
                  {card.descriptionNode && (
                    <Typography
                      level="body-lg"
                      sx={{
                        color: card.id === "favoriteTeacher" ? "neutral.700" : "rgba(255, 255, 255, 0.95)",
                        fontSize: "16px",
                      }}
                    >
                      {card.descriptionNode}
                    </Typography>
                  )}
                </Stack>
                <Stack
                  gap={1}
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop:
                      card.id === "favoriteTeacher"
                        ? "1px solid rgba(0, 0, 0, 0.1)"
                        : "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <Typography
                    level="body-sm"
                    sx={{
                      color: card.id === "favoriteTeacher" ? "neutral.600" : "rgba(255, 255, 255, 0.9)",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    @dance.with.amani
                  </Typography>
                </Stack>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Box sx={{ mt: 2 }}>
        <ShareImageGenerator user={user} stats={data} currentCard={statsCards[activeIndex]} activeIndex={activeIndex} />
      </Box>
    </Stack>
  );
}
